import { FormArray, Validators } from "@angular/forms";
import { SearchResourceType } from "@app/core/model/search/search-resource-type";
import { BasespaceFormControl, BasespaceFormProperties } from "../bs-app-launch-form-control";
import { BsAppLaunchResourceChooserControlComponent } from "./bs-app-launch-resource-chooser-control.component";
import { Subject } from "rxjs";
import { RowAction } from "../bs-app-launch-tabular-field-set-control/tabular-field-set.control";
import { StringUtilities } from "@app/core/utilities/string-utilities";
import { cloneDeep } from "lodash";
import { DatasetSearchColumnFields } from "@app/search/gridOptions/dataset-search-grid-options-formatter";
import { FileSearchColumnFields } from "@app/search/gridOptions/file-search-grid-options-formatter";
import { AppResultSearchColumnFields } from "@app/search/gridOptions/app-result-search-grid-options-formatter";
import { AppResultFileSearchColumnFields } from "@app/search/gridOptions/app-result-file-search-grid-options-formatter";
import { SampleSearchColumnFields } from "@app/search/gridOptions/sample-search-grid-options-formatter";
import { BiosampleSearchColumnFields } from "@app/search/gridOptions/biosample-search-grid-options-formatter";
import { BiosampleLibraryPrepSearchColumnFields } from "@app/search/gridOptions/biosample-library-prep-search-grid-options-formatter";

// Doesn't contain all resource data but has enough to reference/populate the resource
export interface ResourceReference {
    id: number | string;
    name: string;
    type?: string;
    attributes?: any;
}

// Mirrors the backend ControlFilters enum (src/Cloud.FormBuilder/Models/IFilterableControl.cs).
// The backend stamps these into each chooser's `filters` dict on the launchinfo response.
// Only project-filter is ever emitted today; genome-filter is defined in the backend enum
// but never written to the dict, so it is intentionally omitted here.
export enum ControlFilterType {
    ProjectFilter = 'project-filter'
}

// The project column's ag-grid colId per resource type, used to seed a pre-applied (removable)
// project filter on the chooser table. Runs are not project-scoped and have no project column.
// colIds reference each resource type's *SearchColumnFields enum (single source of truth in the
// grid options formatters under src/app/search/gridOptions/) rather than re-declaring strings.
export const PROJECT_FILTER_COL_IDS: { [resourceType: string]: string } = {
    [SearchResourceType.Dataset]: DatasetSearchColumnFields.Project,
    [SearchResourceType.File]: FileSearchColumnFields.Project,
    [SearchResourceType.AppResult]: AppResultSearchColumnFields.Project,
    [SearchResourceType.AppResultFile]: AppResultFileSearchColumnFields.Project,
    [SearchResourceType.Sample]: SampleSearchColumnFields.Project,
    [SearchResourceType.Biosample]: BiosampleSearchColumnFields.DefaultProjectName,
    [SearchResourceType.BiosampleLibraryPrep]: BiosampleLibraryPrepSearchColumnFields.DefaultProjectName
};

export interface ResourceChooserFormControlProperties extends BasespaceFormProperties<BsAppLaunchResourceChooserControlComponent> {
    resourceType: SearchResourceType;
    allowedPermissions: any[];
    multiselect: boolean;
    valueType: 'Input' | 'Output';
    allowResourceCreation: boolean;

    // Only for file chooser
    extensionFilters?: string;
    
    // Only for dataset file chooser. I suspect this was added for backwards compatibility reasons
    datasetFiles?: ResourceReference[];

    // Only for datasets
    datasetResourceInfos?: { id: number; name: string; datasetIdToken: string }[];

    // Only for biosamples
    // There are a bunch of other properties on this field but we don't need them so I left them out in type definition
    datasetsByBioSample?: { 
        libraryPrepKitName: string; 
        libraryPrepKitId: string;
        bioSample: { name: string; id: string };
        attributes: any;
    }[];
    bioSamples?: ResourceReference[];

    // Only for samples
    genomeFilters?: string;

    // JSON-encoded string of filter-name -> value stamped by the backend on the launchinfo
    // response, e.g. "{\"project-filter\":\"21550529\"}". See ControlFilterType.
    filters?: string;

    // This is for initial resources supplied to input from the GET /launchinfo endpoint (for requeuing)
    resources: ResourceReference[];

    searchQuery: string;

    // Can only be following fields: radio buttons, checkboxes, dropdown/select
    displayFields?: BasespaceFormProperties<any>[];
    fieldCells?: BasespaceFormProperties<any>[][];

    displayFieldsTabularFieldSetControl?: FormArray;
    rowActionSubject?: Subject<RowAction>;
    overrideDefaultSize?: string;
}

// Reference docs: https://dagobah-developer.cloud-test.illumina.com/docs/content/documentation/apptools/formbuilder-overview#ProjectChooser
// Reference class: src/Cloud.FormBuilder/Models/RunChooser.cs


export class ResourceChooserFormControl extends BasespaceFormControl<BsAppLaunchResourceChooserControlComponent> implements ResourceChooserFormControlProperties {
    resourceType: SearchResourceType;
    allowedPermissions: any[];
    multiselect: boolean;
    valueType: "Input" | "Output";
    resources: ResourceReference[];
    searchQuery: string;
    allowResourceCreation: boolean;

    displayFields?: BasespaceFormProperties<any>[];
    displayFieldsTabularFieldSetControl?: FormArray;
    fieldCells?: BasespaceFormProperties<any>[][];
    rowActionSubject: Subject<RowAction>;
    overrideDefaultSize?: string;

    // comma separated values ".bam,.vcf"
    extensionFilters?: string;
    datasetFiles?: ResourceReference[];
    genomeFilters?: string;

    // Raw JSON-encoded filters string from the launchinfo response (see parsedFilters getter)
    filters?: string;

    constructor(
        data: ResourceChooserFormControlProperties
    ) {
        const { 
            id, 
            componentReference, 
            label, 
            helpText, 
            size,
            value, 
            required, 
            requiredMessage, 
            resourceType, 
            allowedPermissions, 
            multiselect, 
            valueType, 
            resources,
            datasetFiles,
            datasetResourceInfos,
            datasetsByBioSample,
            bioSamples,
            searchQuery,
            allowResourceCreation,
            extensionFilters,
            tabularFieldSetStyling,
            genomeFilters,
            filters,
            displayFields,
            formControlType
        } = data;
        
        super({ 
            id, 
            componentReference, 
            helpText, 
            size,
            value, 
            required, 
            requiredMessage,
            tabularFieldSetStyling,
            formControlType
        });

        this.resourceType = resourceType;
        this.allowedPermissions = allowedPermissions;
        this.multiselect = multiselect;
        this.valueType = valueType;
        this.displayFields = (displayFields && displayFields.length > 0) ? displayFields : null;

        if(this.displayFields) {
            this.displayFields.forEach((field) => {
                field.tabularFieldSetStyling = true;
            });
        }

        this.overrideDefaultSize = this.displayFields ? '100%' : null;
        

        this.displayFieldsTabularFieldSetControl = this.displayFields ? new FormArray([]) : null;
        this.rowActionSubject = new Subject();

        switch(this.resourceType) {
            case SearchResourceType.File:
                this.resources = datasetFiles;
                break;
            case SearchResourceType.Dataset:
                const reformattedResources = datasetResourceInfos.map((resource) => {
                    return {
                        ...resource,
                        id: resource.datasetIdToken
                    } as ResourceReference
                });
                this.resources = reformattedResources;
                break;
            case SearchResourceType.Biosample:
                // Create flat composite resources for each biosample+libraryprep combination
                const flatBiosamples: ResourceReference[] = [];
                const seenCompositeIds = new Set<string>();
                
                datasetsByBioSample.forEach((datasetByBiosample) => {
                    const { libraryPrepKitName, libraryPrepKitId, bioSample, attributes } = datasetByBiosample;
                    const baseBiosample = bioSamples.find((bs) => bs.id === bioSample.id);
                    const compositeId = `${bioSample.id}::${libraryPrepKitId}`;
                    
                    // Only add if this composite ID doesn't already exist
                    if (!seenCompositeIds.has(compositeId)) {
                        seenCompositeIds.add(compositeId);
                        flatBiosamples.push({
                            id: compositeId,
                            name: `${baseBiosample.name}: ${libraryPrepKitName}`,
                            attributes: attributes
                        });
                    }
                });
                this.resources = flatBiosamples;
                break;
            case SearchResourceType.Sample:
                this.genomeFilters = genomeFilters;

                // We don't want to populate sample chooser with extrapolated sample data from biosamples
                // ie user submitted form with biosamples but they requeued the analysis with "input data" set to "samples..."
                if(!bioSamples || bioSamples.length === 0) {
                    this.resources = resources;
                }
                break;
            default:
                this.resources = resources;
        }
        
        this.label = this.getReplacementText(label || '');
        this.searchQuery = searchQuery;

        this.allowResourceCreation = allowResourceCreation || false;
        this.extensionFilters = extensionFilters ? extensionFilters : null;
        this.filters = filters ? filters : null;

        if(required) {
            this.setValidators(Validators.required);
        }

        if(this.resources) {
            const value = this.resources.map((resource) => resource.id);
            this.setValue(value);
        }

        if(this.displayFields && this.resources && this.resources.length > 0) {
            const fieldCells = [];
            this.resources.forEach((resource) => {
                const fieldRow = [];

                this.displayFields.forEach((field) => {
                    const clonedField = cloneDeep(field);
                    const fieldKey = StringUtilities.findKeyIgnoreCase(resource.attributes, `input.${StringUtilities.toCamelCase(field.id)}`);
                    const fieldValueArray = fieldKey ? resource.attributes[fieldKey] : null;
                    const fieldValue = fieldValueArray ? fieldValueArray[0] : null;
                    if(fieldValue) {
                        fieldRow.push(this.setFieldValue(clonedField, fieldValue));
                    } else {
                        fieldRow.push(clonedField);
                    }
                });

                fieldCells.push(fieldRow);
            });

            this.fieldCells = fieldCells;
        }
    }

    /**
     * Parses the raw `filters` JSON string from the launchinfo response into a
     * filter-name -> value map (e.g. { "project-filter": "21550529" }).
     * Parses defensively: a missing or malformed `filters` value soft-fails to an
     * empty object so a bad filter can never break form initialization.
     */
    get parsedFilters(): { [filterName: string]: string } {
        if (this.filters == null || this.filters === '') {
            return {};
        }

        try {
            const parsed = JSON.parse(this.filters);
            return (parsed && typeof parsed === 'object') ? parsed : {};
        } catch (error) {
            return {};
        }
    }

    /**
     * A hardcoded, app-launch-only base clause that always applies for this resource type
     * regardless of what the app developer or consumer specifies. For biosamples we only ever
     * want FASTQ-backed biosamples on the launch form.
     */
    private get resourceTypeBaseQuery(): string {
        switch (this.resourceType) {
            case SearchResourceType.Biosample:
            case SearchResourceType.BiosampleLibraryPrep:
                return 'HasQcPassedFastqDatasets:true';
            default:
                return '';
        }
    }

    /**
     * The "always applies" base query for this chooser: the static resource-type clause only.
     * The project-filter is intentionally NOT part of this query - it is a removable, user-facing
     * preselection applied as a column filter on the chooser table (see projectFilterId /
     * projectColId), not a hard scope. The component passes this straight to SearchQueryService
     * as the base query, where it is AND-combined with the user's text and resource-dictionary
     * base query.
     */
    get baseExtendedQuery(): string {
        return this.resourceTypeBaseQuery;
    }

    /**
     * The project Id the backend stamped on this chooser's `filters` payload (e.g. "21550529"),
     * or null when absent. This is surfaced as a removable, pre-applied column filter on the
     * chooser table rather than folded into the base query. Runs are not project-scoped, so a
     * stamped project-filter is intentionally ignored for them.
     */
    get projectFilterId(): string | null {
        if (this.resourceType === SearchResourceType.Run) {
            return null;
        }

        const value = this.parsedFilters[ControlFilterType.ProjectFilter];
        return (value != null && value !== '') ? value : null;
    }

    /**
     * The ag-grid colId of the project column for this resource type, used to seed the
     * pre-applied project filter on the chooser table. null when the resource type has no
     * project column (e.g. runs).
     */
    get projectFilterColId(): string | null {
        return PROJECT_FILTER_COL_IDS[this.resourceType as string] || null;
    }

    /**
     * Sets the value on a field properties object based on its formControlType.
     * For Select/RadioButton controls, sets the appropriate choice's `selected` property.
     * For CheckBox controls, sets the appropriate choice's `checked` property.
     * For other controls, sets the `value` property directly.
     * 
     * @param field The field properties object to update
     * @param value The value to set
     * @returns The modified field properties object
     */
    setFieldValue(field: BasespaceFormProperties<any>, value: any): BasespaceFormProperties<any> {
        switch(field.formControlType) {
            case 'Select':
                if (field['choices']) {
                    field['choices'].forEach((choice: { selected: boolean; value: any }) => {
                        choice.selected = choice.value === value;
                    });
                }
                break;
            case 'RadioButton':
                field['value'] = value;
                break;
            case 'CheckBox':
                // For CheckBox, set checked = true for matching values (can be array for multiselect)
                if (field['choices']) {
                    const valuesToCheck = Array.isArray(value) ? value : [value];
                    field['choices'].forEach((choice: { checked: boolean; value: any }) => {
                        choice.checked = valuesToCheck.includes(choice.value);
                    });
                }
                break;
            default:
                // For other control types, set value directly
                field.value = value;
                break;
        }

        return field;
    }

    transformResourceIdToFormDataId(id: string): string {
        switch(this.resourceType) {
            case SearchResourceType.File:
                return `dsf.${id}`;
            case SearchResourceType.Dataset:
                return `dsit.${id}`
            default:
                return id;
        }
    }


    getReplacementText(input: string): string {
        switch(this.resourceType) {
            case SearchResourceType.Dataset:
                return input.replace("an app result", "a dataset").replace("an App Result", "a Dataset")
                    .replace("app result", "dataset").replace("App Result", "Dataset");  
            case SearchResourceType.AppResult:
                return input.replace("a dataset", "an app result").replace("a Dataset", "an App Result")
                    .replace("dataset", "app result").replace("Dataset", "App Result"); 
            case SearchResourceType.Biosample:
                // Use negative lookbehind to only replace "sample" when not preceded by "bio"
                return input.replace(/(?<!bio)sample/gi, (match) => {
                    return match === 'sample' ? 'biosample' : 'Biosample';
                });
            case SearchResourceType.Sample:
                return input.replace(/biosample/gi, (match) => {
                    return match === 'biosample' ? 'sample' : 'Sample';
                }); 
            default:
                return input;
        }
    }

    getDisplayFieldsRequestEntries(): any[][] {
        const requestEntries = [];

        if(this.displayFieldsTabularFieldSetControl) {
            this.displayFieldsTabularFieldSetControl.controls.forEach((row: FormArray, rowIndex: number) => {
                const resource = this.resources[rowIndex];
                row.controls.forEach((field: BasespaceFormControl<any>) => {
                    requestEntries.push(
                        [
                            `${this.id}.${this.resourceType}.${this.transformResourceIdToFormDataId(String(resource.id))}.${field.fieldId}`,
                            field.value
                        ]
                    );
                });
            });
        }

        return requestEntries;
    }

    getRequestEntries(): any[][] {
        const displayFieldRequestEntries = this.getDisplayFieldsRequestEntries();

        if(this.value){
            const resourceRequestEntries = this.value.map((value) => {
                return [this.id, this.transformResourceIdToFormDataId(value)];
            });
            return [...resourceRequestEntries, ...displayFieldRequestEntries];
        } else {
            return [];
        }
    }
    
}